import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";

import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import AppleIcon from "@mui/icons-material/Apple";
import GoogleIcon from "@mui/icons-material/Google";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import router from "next/router";
import { ApiStatusResponseType } from "@/types/Profile/ApiStatusResponseType";
import { setCookie } from "cookies-next";

import { GoogleAuthProvider, OAuthProvider, signInWithPopup, User } from "firebase/auth";
import { auth } from "@/utils/firebaseConfig";
import { GoogleSignInType } from "@/types/auth/GoogleSignInType";
import { DeviceType } from "@/types/enumTypes";
import { authSlice } from "@/store/authSlice";
import { responseMessageSlice } from "@/store/responseMessageSlice";
import { useAppDispatch } from "@/store/store";
import { MFA_TOKEN, REFRESHTOKEN, SUCCESS, TOKEN } from "@/utils/constants";
import { googleEvent } from '@/lib/gtag';
import { AppleSignInType } from "@/types/auth/AppleSignInType";

interface ReduxSignInCardProps {
  onSignIn?: (email: string) => void;
  onAppleSignIn?: () => void;
}


const ReduxSignInCard: React.FC<ReduxSignInCardProps> = ({
  onSignIn,
}) => {
  const dispatch = useAppDispatch();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const validateEmail = (value: string) => {
    if (!value.trim()) {
      return "Email address is required";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(value)) {
      return "Please enter a valid email address";
    }

    return "";
  };

  function getDisplayName(user: User, provider: string) {
    let name = '';
    if (
      user?.displayName !== undefined &&
      user?.displayName !== null &&
      user?.displayName !== ''
    ) {
      name = user?.displayName ?? '';
    } else if (
      user.providerData !== undefined &&
      user.providerData.filter((x) => x.providerId === provider).length > 0
    ) {
      name =
        user.providerData.filter((x) => x.providerId === provider)[0]
          ?.displayName ?? '';
    }
    return name;
  }

  function getEmailAddress(user: User, provider: string) {
    let email = '';
    if (
      user?.email !== undefined &&
      user?.email !== null &&
      user?.email !== ''
    ) {
      email = user?.email ?? '';
    } else if (
      user.providerData !== undefined &&
      user.providerData.filter((x) => x.providerId === provider).length > 0
    ) {
      email =
        user.providerData.filter((x) => x.providerId === provider)[0]?.email ??
        '';
    }
    return email;
  }

  const handleEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;

    setEmail(value);

    // Clear error while typing
    if (emailError) {
      setEmailError(validateEmail(value));
    }
  };

  const handleSignIn = () => {
    const error = validateEmail(email);

    if (error) {
      setEmailError(error);
      return;
    }

    setEmailError("");
    router.push(
      `${process.env.NEXT_PUBLIC_APPLICATION_URL}signin?emailaddress=${email}`
    );
    onSignIn?.(email);
  };

  const onGoogleSignIn = () => {
    signInWithPopup(auth, new GoogleAuthProvider())
      .then(async (result) => {
        // This gives you a Google Access Token. You can use it to access the Google API.
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const googleAuthId = credential?.accessToken;
        const { user } = result;

        const name = getDisplayName(user, 'google.com');
        const email = getEmailAddress(user, 'google.com');

        const googleSignInData: GoogleSignInType = {
          EmailAddress: email ?? '',
          ContactName: name ?? '',
          PhoneNumber: '',
          DeviceType: DeviceType.Web,
          GoogleAuthId: googleAuthId ?? '',
          IsTaxProfessional: false,
          TaxProfessionalPtin: '',
          SignupFrom: '',
          IsEmailBounced: false,
          ReferralCode: '',
          IsWhitecLabelUser: false,
        };

        const postData: GoogleSignInType = googleSignInData;

        const address = `/api/GoogleSignIn/`;

        const socialMediaResponse = await fetch(address, {
          method: 'POST',
          body: JSON.stringify(postData),
        });

        const response = await socialMediaResponse.json();

        dispatch(authSlice.actions.setAuthState(false));

        if (response.status === SUCCESS) {
          if (response.IsMfaAuthentication === true) {
            dispatch(
              authSlice.actions.setEmailAddress(response.EmailAddress)
            );

            dispatch(
              authSlice.actions.setLoggedInName(response.ContactName)
            );

            dispatch(
              authSlice.actions.setIsTwoStepVerification(
                response.IsMfaAuthentication
              )
            );

            setCookie(MFA_TOKEN, response?.MfaToken, {
              domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
              path: '/',
              secure: true,
              sameSite: 'lax',
            } as any);

            router.push(
              `${process.env.NEXT_PUBLIC_APPLICATION_URL}two-step-authentication/`
            );

          } else if (response?.Token === '') {
            dispatch(
              authSlice.actions.setEmailAddress(email)
            );

            dispatch(
              authSlice.actions.setLoggedInName(name)
            );

            dispatch(
              authSlice.actions.setGoogleAuthId(
                googleAuthId ?? ''
              )
            );

            dispatch(
              authSlice.actions.setIsTwoStepVerification(
                response.IsMfaAuthentication
              )
            );

            router.push(
              `${process.env.NEXT_PUBLIC_APPLICATION_URL}oauth/`
            );
          } else if (response?.Token !== '') {
            googleEvent({
              action: 'login',
              params: {
                method: 'Google',
              },
            });

            dispatch(
              authSlice.actions.setEmailAddress(email)
            );

            dispatch(
              authSlice.actions.setLoggedInName(name)
            );

            dispatch(
              authSlice.actions.setGoogleAuthId(
                googleAuthId ?? ''
              )
            );

            // Sign In Flow
            dispatch(
              authSlice.actions.setAuthState(true)
            );

            dispatch(
              authSlice.actions.setToken(response?.Token)
            );

            dispatch(
              authSlice.actions.setRefreshToken(
                response?.RefreshToken
              )
            );

            dispatch(
              authSlice.actions.setRefreshTokenId(
                response?.RefreshTokenDetailId
              )
            );

            dispatch(
              authSlice.actions.setIsTaxProfessional(
                response.IsTaxProfessional
              )
            );

            dispatch(
              authSlice.actions.setIsTwoStepVerification(
                response.IsMfaAuthentication
              )
            );

            dispatch(
              authSlice.actions.setIsWorkFlowSettingEnabled(
                response.IsWorkFlowSettingEnabled
              )
            );

            dispatch(
              authSlice.actions.setUserAccountCount(
                response.UserAccountCount
              )
            );

            dispatch(
              authSlice.actions.setIsEmailVerified(
                response.IsEmailBounced !== true
              )
            );

            setCookie(TOKEN, response?.Token, {
              domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
              path: '/',
              secure: true,
              sameSite: 'lax',
            } as any);

            setCookie(REFRESHTOKEN, response?.RefreshToken, {
              domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
              path: '/',
              secure: true,
              sameSite: 'lax',
            } as any);

            if (response.UserAccountCount > 0) {
              window.location.href = '/manage-account/';
            } else {
              router.push(
                `${process.env.NEXT_PUBLIC_APPLICATION_URL}dashboard/`
              );
            }
          }
        } else {
          const snackBarObject: ApiStatusResponseType = {
            status: response?.status,
            statuscode: response?.statuscode,
            messages: response?.messages,
          };

          dispatch(
            responseMessageSlice.actions.setResponseMessage(
              snackBarObject
            )
          );
        }

        /*
            })
            .catch(() => {});
        });
        */

      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessages = error.message;
        const { email } = error;
        const credential =
          GoogleAuthProvider.credentialFromError(error);

        // eslint-disable-next-line no-console
        console.log({
          errorCode,
          errorMessages,
          email,
          credential,
        });
      });
  };

  const onAppleSignIn = () => {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    provider.setCustomParameters({
      // Localize the Apple authentication screen in French.
      locale: 'en',
    });


    signInWithPopup(auth, provider)
      .then(async (result) => {
        // The signed-in user info.
        const { user } = result;

        // Apple credential
        const credential = OAuthProvider.credentialFromResult(result);
        const accessToken = user.providerData.filter(
          (x) => x.providerId === 'apple.com'
        )[0]?.uid;
        const idToken = credential?.idToken;
        const name = getDisplayName(user, 'apple.com');
        const email = getEmailAddress(user, 'apple.com');

        const appleSignInData: AppleSignInType = {
          EmailAddress: email ?? '',
          ContactName: name,
          PhoneNumber: '',
          DeviceType: DeviceType.Web,
          AppleAuthId: idToken ?? '',
          AppleToken: accessToken ?? '',
          IsTaxProfessional: false,
          TaxProfessionalPtin: '',
          SignupFrom: '',
          IsEmailBounced: false,
          ReferralCode: '',
          IsWhitecLabelUser: false,
        };

        const postData: AppleSignInType = appleSignInData;

        const address = `/api/AppleSignIn/`;

        const socialMediaResponse = await fetch(address, {
          method: 'POST',
          body: JSON.stringify(postData),
        });

        const response = await socialMediaResponse.json();

        dispatch(authSlice.actions.setAuthState(false));

        if (response.status === SUCCESS) {
          if (response.IsMfaAuthentication === true) {
            dispatch(
              authSlice.actions.setEmailAddress(response.EmailAddress)
            );

            dispatch(
              authSlice.actions.setLoggedInName(response.ContactName)
            );

            dispatch(
              authSlice.actions.setIsTwoStepVerification(
                response.IsMfaAuthentication
              )
            );

            setCookie(MFA_TOKEN, response?.MfaToken, {
              domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
              path: '/',
              secure: true,
              sameSite: 'lax',
            } as any);

            router.push(
              `${process.env.NEXT_PUBLIC_APPLICATION_URL}two-step-authentication/`
            );

          } else if (response?.Token === '') {
            dispatch(
              authSlice.actions.setEmailAddress(email)
            );

            dispatch(
              authSlice.actions.setLoggedInName(name)
            );

            dispatch(
              authSlice.actions.setAppleAuthId(
                idToken ?? ''
              )
            );

            dispatch(
              authSlice.actions.setIsTwoStepVerification(
                response.IsMfaAuthentication
              )
            );

            router.push(
              `${process.env.NEXT_PUBLIC_APPLICATION_URL}oauth/`
            );
          } else if (response?.Token !== '') {
            googleEvent({
              action: 'login',
              params: {
                method: 'Apple',
              },
            });

            dispatch(
              authSlice.actions.setEmailAddress(email)
            );

            dispatch(
              authSlice.actions.setLoggedInName(name)
            );

            dispatch(
              authSlice.actions.setAppleAuthId(
                idToken ?? ''
              )
            );

            // Sign In Flow
            dispatch(
              authSlice.actions.setAuthState(true)
            );

            dispatch(
              authSlice.actions.setToken(response?.Token)
            );

            dispatch(
              authSlice.actions.setRefreshToken(
                response?.RefreshToken
              )
            );

            dispatch(
              authSlice.actions.setRefreshTokenId(
                response?.RefreshTokenDetailId
              )
            );

            dispatch(
              authSlice.actions.setIsTaxProfessional(
                response.IsTaxProfessional
              )
            );

            dispatch(
              authSlice.actions.setIsTwoStepVerification(
                response.IsMfaAuthentication
              )
            );

            dispatch(
              authSlice.actions.setIsWorkFlowSettingEnabled(
                response.IsWorkFlowSettingEnabled
              )
            );

            dispatch(
              authSlice.actions.setUserAccountCount(
                response.UserAccountCount
              )
            );

            dispatch(
              authSlice.actions.setIsEmailVerified(
                response.IsEmailBounced !== true
              )
            );

            setCookie(TOKEN, response?.Token, {
              domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
              path: '/',
              secure: true,
              sameSite: 'lax',
            } as any);

            setCookie(REFRESHTOKEN, response?.RefreshToken, {
              domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
              path: '/',
              secure: true,
              sameSite: 'lax',
            } as any);

            if (response.UserAccountCount > 0) {
              window.location.href = '/manage-account/';
            } else {
              router.push(
                `${process.env.NEXT_PUBLIC_APPLICATION_URL}dashboard/`
              );
            }
          }
        } else {
          const snackBarObject: ApiStatusResponseType = {
            status: response?.status,
            statuscode: response?.statuscode,
            messages: response?.messages,
          };

          dispatch(
            responseMessageSlice.actions.setResponseMessage(
              snackBarObject
            )
          );
        }

        /*
            })
            .catch(() => {});
        });
        */

      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessages = error.message;
        const { email } = error;
        // const credential =
        //   AppleAuthProvider.credentialFromError(error);

        // eslint-disable-next-line no-console
        console.log({
          errorCode,
          errorMessages,
          email
        });
      });
  };


  return (

    <Card
      elevation={0}
      sx={{
        width: "100%",
        py: 2, px: 2.5,
        borderRadius: "10px",
        backgroundColor: "#fff",
        border: "1px solid #e2e6ed",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        mb: 2
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: "#14181f",
          mb: 1,
        }}
      >
        Sign In
      </Typography>

      {/* Email Address */}
      <TextField
        fullWidth
        required
        size="small"
        type="email"
        placeholder="Email Address"
        value={email}
        onChange={handleEmailChange}
        error={Boolean(emailError)}
        helperText={emailError}
        onBlur={() => {
          setEmailError(validateEmail(email));
        }}
        sx={{
          marginBottom: emailError ? "8px" : "12px",

          "& .MuiOutlinedInput-root": {
            height: "40px",
            borderRadius: "6px",
            fontSize: 14,

            "& fieldset": {
              borderColor: "#d7dce5",
            },

            "&:hover fieldset": {
              borderColor: "#b9c1ce",
            },

            "&.Mui-focused fieldset": {
              borderColor: "#2945a3",
            },

            "&.Mui-error fieldset": {
              borderColor: "#d32f2f",
            },
          },

          "& input::placeholder": {
            color: "#64748b",
            opacity: 1,
          },

          "& .MuiFormHelperText-root": {
            marginLeft: "2px",
            fontSize: "11px",
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <EmailOutlinedIcon
                sx={{
                  fontSize: 16,
                  color: emailError ? "#d32f2f" : "#526174",
                }}
              />
            </InputAdornment>
          ),
        }}
      />

      {/* Sign In */}
      <Button
        fullWidth
        variant="contained"
        onClick={handleSignIn}
        startIcon={
          <LockOutlinedIcon

          />
        }
        sx={{
          borderRadius: "6px",
          backgroundColor: "#2945a3",
          textTransform: "none",
          fontWeight: 600,
          boxShadow: "none",

          "&:hover": {
            backgroundColor: "#233b8d",
            boxShadow: "none",
          },
        }}
      >
        Sign In
      </Button>

      {/* Sign In Via */}
      <Typography
        sx={{
          textAlign: "center",
          fontSize: 11,
          letterSpacing: "0.7px",
          color: "#64748b",
          mt: 2,
          mb: 1.5,
        }}
      >
        SIGN IN VIA
      </Typography>

      {/* Social Login */}
      <Box
        sx={{
          display: "flex",
          gap: "10px",
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          onClick={onAppleSignIn}
          sx={{
            height: "38px",
            minWidth: 0,
            borderRadius: "6px",
            borderColor: "#d9dee7",
            color: "#111827",
          }}
        >
          <AppleIcon sx={{ fontSize: 19 }} />
        </Button>

        <Button
          fullWidth
          variant="outlined"
          onClick={onGoogleSignIn}
          sx={{
            height: "38px",
            minWidth: 0,
            borderRadius: "6px",
            borderColor: "#d9dee7",
            color: "#111827",
          }}
        >
          <GoogleIcon
            sx={{
              fontSize: 18,
              //  color: "#4285F4",
            }}
          />
        </Button>
      </Box>
    </Card>

  );
};

export default ReduxSignInCard;